import React from 'react';
import { Outlet, RouteObject } from 'react-router-dom';
import { useCapabilities } from '../common/hooks/useCan';
import { isCapabilityUiEnabled } from '../library/Core/Capabilities';
import LoadingSpinner from '../library/SimpleControls/LoadingSpinner';

/**
 * Route-level capability guard (role capabilities, Phase 1b). Declare the requirement on the route:
 *
 *   { path: 'providers', element: <ProviderList />, handle: { capability: 'business_directory.provider.view' } }
 *   { path: 'reports',   element: <Reports />,      handle: { anyOf: ['reports.a.view', 'reports.b.view'] } }
 *
 * and wrap the route tree once with withCapabilityGuards(routes) (done where the router is created).
 * Routes without `handle.capability` / `handle.anyOf` are returned untouched. Convenience only: the API enforces the
 * same capability. With REACT_APP_CAPABILITY_UI off nothing changes.
 */
export type CapabilityRouteHandle = { capability?: string; anyOf?: string[] };

type GateProps = CapabilityRouteHandle & { children?: React.ReactNode };

export const CapabilityRoute = ({ capability, anyOf, children }: GateProps) => {
    const { can, canAny, loaded } = useCapabilities();

    if (!isCapabilityUiEnabled()) return <>{children}</>;
    if (!loaded) return <LoadingSpinner />;

    const allowed = capability !== undefined ? can(capability) : canAny(anyOf ?? []);
    if (allowed) return <>{children}</>;

    return (
        <div className="container py-5 text-center" data-testid="capability-denied">
            <h4>Access denied</h4>
            <p>You do not have permission to view this page.</p>
        </div>
    );
};

const handleOf = (route: RouteObject): CapabilityRouteHandle | null => {
    const handle = route.handle as CapabilityRouteHandle | undefined;
    if (!handle) return null;
    if (typeof handle.capability === 'string' && handle.capability) return { capability: handle.capability };
    if (Array.isArray(handle.anyOf) && handle.anyOf.length > 0) return { anyOf: handle.anyOf };
    return null;
};

const guard = (route: RouteObject): RouteObject => {
    const original = route.children;
    const children = original ? original.map(guard) : undefined;
    const childrenChanged = !!children && !!original && children.some((c, i) => c !== original[i]);
    const requirement = handleOf(route);
    if (!requirement && !childrenChanged) return route;

    const next: any = { ...route };
    if (childrenChanged) next.children = children;

    if (requirement) {
        if (route.lazy && !route.element && !route.Component) {
            console.error('withCapabilityGuards: a lazy route cannot be guarded, give it an element', route.path);
            return next;
        }
        const inner = route.element ?? (route.Component ? React.createElement(route.Component) : <Outlet />);
        next.element = <CapabilityRoute {...requirement}>{inner}</CapabilityRoute>;
        delete next.Component;
    }
    return next;
};

/** Wrap every route that declares handle.capability / handle.anyOf (at any depth) in a CapabilityRoute. */
export const withCapabilityGuards = (routes: RouteObject[]): RouteObject[] => routes.map(guard);
